import { lazyWithRecovery } from "../lib/chunk-load-recovery/lazy-with-recovery";

import type { RouteObject } from "react-router-dom";
import { Navigate, Outlet } from "react-router-dom";

import { AnonymousOnlyGuard } from "../app/router/guards/components/AnonymousOnlyGuard";
import DefaultLayout from "../components/layouts/default-layout/DefaultLayout";
import RouteWrapper from "../components/layouts/route-wrapper/RouteWrapper";
import {
  CHOOSE_PASSWORD_PATH,
  EMAIL_VERIFICATION_SENT_PATH,
  FORGOT_PASSWORD_EMAIL_SENT_PATH,
  FORGOT_PASSWORD_PATH,
  LOGIN_ERROR_SOCIAL_PATH,
  LOGIN_PATH,
  REGISTER_PATH,
  WELCOME_PATH,
} from "../constants/env";
import { RouteWrapperVariant } from "../constants/layouts/route-wrapper-variant.enum";
import { SendGtmEvents } from "../legacy/components/SendGtmEvents";
import SEO from "../legacy/components/Seo";
import { ShowStickyError } from "../legacy/components/ShowStickyError";
import { GTMEventCollection } from "../legacy/services/gtmEvent/collection/gtmEventCollection";
import { withSuspense } from "./protected-routes/utils/with-suspense";

const ChoosePasswordPage = lazyWithRecovery(() => import("../legacy/pages/Auth/ChoosePasswordPage"));
const EmailVerificationSentPage = lazyWithRecovery(
  () => import(/* webpackPreload: true */ "../legacy/pages/Auth/EmailVerificationSentPage")
);
const ForgotPasswordEmailSentPage = lazyWithRecovery(() => import("../legacy/pages/Auth/ForgotPasswordEmailSentPage"));
const ForgotPasswordPage = lazyWithRecovery(() => import("../legacy/pages/Auth/ForgotPasswordPage"));
const LoginPage = lazyWithRecovery(() => import(/* webpackPreload: true */ "../legacy/pages/Auth/LoginPage"));
const RegisterPage = lazyWithRecovery(() => import(/* webpackPreload: true */ "../legacy/pages/Auth/RegisterPage"));
const WelcomePage = lazyWithRecovery(() => import(/* webpackPreload: true */ "../legacy/pages/Auth/WelcomePage"));

export function createPublicRoutes(): RouteObject[] {
  return [
    {
      path: "/",
      element: (
        <DefaultLayout>
          <Outlet />
        </DefaultLayout>
      ),
      children: [
        {
          path: LOGIN_ERROR_SOCIAL_PATH,
          element: (
            <>
              <ShowStickyError />
              <Navigate to={WELCOME_PATH} replace />
            </>
          ),
        },
        {
          element: (
            <AnonymousOnlyGuard>
              <Outlet />
            </AnonymousOnlyGuard>
          ),
          children: [
            {
              path: WELCOME_PATH,
              element: (
                <>
                  <SEO title="Benvenuto" />
                  <SendGtmEvents events={[{ event: GTMEventCollection.signUpWelcome }]} />
                  <RouteWrapper variant={RouteWrapperVariant.fullScreen}>{withSuspense(<WelcomePage />)}</RouteWrapper>
                </>
              ),
            },
            {
              path: LOGIN_PATH,
              element: (
                <>
                  <SEO title="Login" />
                  <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRouteWithBackButton}>
                    {withSuspense(<LoginPage />)}
                  </RouteWrapper>
                </>
              ),
            },
            {
              path: FORGOT_PASSWORD_PATH,
              element: (
                <>
                  <SEO title="Password dimenticata" />
                  <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRouteWithBackButton}>
                    {withSuspense(<ForgotPasswordPage />)}
                  </RouteWrapper>
                </>
              ),
            },
            {
              path: FORGOT_PASSWORD_EMAIL_SENT_PATH,
              element: (
                <>
                  <SEO title="Email ripristino password" />
                  <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRoute}>
                    {withSuspense(<ForgotPasswordEmailSentPage />)}
                  </RouteWrapper>
                </>
              ),
            },
            {
              path: REGISTER_PATH,
              element: (
                <>
                  <SEO title="Registrazione" />
                  <SendGtmEvents events={[{ event: GTMEventCollection.signUpRegister, onceForSession: true }]} />
                  <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRouteWithBackButton}>
                    {withSuspense(<RegisterPage />)}
                  </RouteWrapper>
                </>
              ),
            },
          ],
        },
        {
          path: CHOOSE_PASSWORD_PATH,
          element: (
            <>
              <SEO title="Cambia password" />
              <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRoute}>
                {withSuspense(<ChoosePasswordPage />)}
              </RouteWrapper>
            </>
          ),
        },
        {
          path: EMAIL_VERIFICATION_SENT_PATH,
          element: (
            <>
              <SEO title="Invio verifica email" />
              <RouteWrapper variant={RouteWrapperVariant.unauthenticatedRoute}>
                {withSuspense(<EmailVerificationSentPage />)}
              </RouteWrapper>
            </>
          ),
        },
      ],
    },
  ];
}
